// 利用者の位置を決める共通処理。
// 優先順：① 端末の現在地 → ② プロフィールに登録した地域（文字列をジオコーディング）→ ③ なし。
// 特定の都市への固定フォールバックはしない（全国の利用者が使う前提）。
import React from 'react';

export type UserLocation = {
  lat: number;
  lng: number;
  source: 'device' | 'profile';
  label: string; // 表示用（「現在地」またはプロフィールの地域名）
};

type LatLng = { lat: number; lng: number };

let devicePromise: Promise<LatLng | null> | null = null;

/** 端末の現在地を一度だけ取得してキャッシュする（取れなければ null） */
export function getDeviceLocation(): Promise<LatLng | null> {
  if (devicePromise) return devicePromise;
  devicePromise = new Promise<LatLng | null>((resolve) => {
    if (typeof navigator === 'undefined' || !navigator.geolocation) { resolve(null); return; }
    let done = false;
    const finish = (v: LatLng | null) => { if (!done) { done = true; resolve(v); } };
    const timer = setTimeout(() => finish(null), 5000);
    navigator.geolocation.getCurrentPosition(
      (pos) => { clearTimeout(timer); finish({ lat: pos.coords.latitude, lng: pos.coords.longitude }); },
      () => { clearTimeout(timer); finish(null); },
      { timeout: 4500, maximumAge: 30 * 60 * 1000 },
    );
  });
  // 取得に失敗した場合は、次の画面表示で再挑戦できるようにする
  devicePromise.then((v) => { if (!v) devicePromise = null; });
  return devicePromise;
}

const GEO_CACHE_PREFIX = 'ki_geo_v1:';
const geocodeInflight: Record<string, Promise<LatLng | null>> = {};

/** プロフィールの地域名（例：東京都新宿区）を緯度経度に変換する。結果は端末に保存して再利用 */
export function geocodePlace(place: string): Promise<LatLng | null> {
  const key = place.trim();
  if (!key || key === '取得済み' || key === '現在地') return Promise.resolve(null);
  try {
    const hit = localStorage.getItem(GEO_CACHE_PREFIX + key);
    if (hit) { const v = JSON.parse(hit); return Promise.resolve(v && typeof v.lat === 'number' ? v : null); }
  } catch { /* localStorage が使えない環境 */ }
  if (geocodeInflight[key] !== undefined) return geocodeInflight[key];
  const url = `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(key)}&format=json&limit=1&countrycodes=jp&accept-language=ja`;
  geocodeInflight[key] = fetch(url)
    .then((r) => (r.ok ? r.json() : []))
    .then((arr: any[]) => {
      const v = arr && arr[0] ? { lat: parseFloat(arr[0].lat), lng: parseFloat(arr[0].lon) } : null;
      const ok = v && isFinite(v.lat) && isFinite(v.lng) ? v : null;
      try { localStorage.setItem(GEO_CACHE_PREFIX + key, JSON.stringify(ok)); } catch { /* noop */ }
      return ok;
    })
    .catch(() => null)
    .finally(() => { delete geocodeInflight[key]; });
  return geocodeInflight[key];
}

/** 現在地から「都道府県＋市区町村」の文字列を作る（プロフィール登録用） */
export async function placeFromDevice(): Promise<string | null> {
  const dev = await getDeviceLocation();
  if (!dev) return null;
  try {
    const r = await fetch(`https://nominatim.openstreetmap.org/reverse?lat=${dev.lat}&lon=${dev.lng}&format=json&accept-language=ja`);
    const d = await r.json();
    const city = d.address?.city || d.address?.town || d.address?.village || d.address?.county || '';
    const pref = d.address?.state || d.address?.province || '';
    const place = `${pref}${city}`;
    if (!place) return null;
    try { localStorage.setItem(GEO_CACHE_PREFIX + place, JSON.stringify(dev)); } catch { /* noop */ }
    return place;
  } catch { return null; }
}

export async function resolveUserLocation(profileLocation?: string | null): Promise<UserLocation | null> {
  const dev = await getDeviceLocation();
  if (dev) return { ...dev, source: 'device', label: '現在地' };
  if (profileLocation) {
    const g = await geocodePlace(profileLocation);
    if (g) return { ...g, source: 'profile', label: profileLocation.trim() };
  }
  return null;
}

/** 位置を解決する React フック。loading 中は undefined、見つからなければ null */
export function useUserLocation(profileLocation?: string | null): UserLocation | null | undefined {
  const [loc, setLoc] = React.useState<UserLocation | null | undefined>(undefined);
  React.useEffect(() => {
    let alive = true;
    resolveUserLocation(profileLocation).then((v) => { if (alive) setLoc(v); });
    return () => { alive = false; };
  }, [profileLocation]);
  return loc;
}
